import { z } from "zod";
import { isVnCalendarYmd, vnDayEndExclusiveUtc, vnDayStartUtc, vnYmdOf } from "@goppy/domain";
import { AI_PAGE_ID_MAX_CHARS, AI_PAGE_ID_RE } from "@goppy/domain";

/**
 * Shared DTO cho Admin AI audit-trace + safety board (US-AE8.F4-0028 · @gate ai+pii):
 *  - @UC9073 ai-audit-trace  — GET /admin/ai/audit-trace (list: prompt → intent → amount →
 *    idempotencyKey → ledger_transaction; read-only join ledger).
 *  - @UC9075 ai-safety-board — GET /admin/ai/safety-events (list ca an toàn bị chặn + filter kind).
 *
 * Một nguồn sự thật shape cho api (S01) + admin web (S03) + e2e (S04) — tránh drift (DRY).
 *
 * BẢO MẬT / BẤT BIẾN (invariants):
 * - `rawPrompt` REDACTION-AWARE: nullable — chỉ SUPER_ADMIN thấy thô; vai khác server trả null
 *   (DR-2). Contract KHÔNG ép lộ PII; enforce redaction ở S01 (server).
 * - `proposalAmountMinor` = BigInt minor VND → STRING trên wire (money no-float). TỪ fact-block
 *   (API), KHÔNG do LLM tính (AI-INV-2). null cho intent read-only.
 * - `ai_audit_log` + ledger APPEND-ONLY (AI-INV-7 + G2) — chỉ có endpoint READ; KHÔNG shape mutate.
 * - Safety `kind` taxonomy đóng: injection | tool_scope | fact_block | escalation (DR-3).
 * - `.strict()` — KHÔNG khai báo field lạ; reject tại biên (chống leak).
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 */

/** Max page-size cho list audit-trace + safety. Default (20) do backend áp khi vắng. */
export const ADMIN_AI_AUDIT_MAX_LIMIT = 100;
export const ADMIN_AI_SAFETY_MAX_LIMIT = 100;

// ── audit-trace ──────────────────────────────────────────────────────────────────────────────

/** Kết cục một tương tác chat AI. */
export const aiAuditOutcomeSchema = z.enum(["executed", "proposed", "clarify", "blocked"]);
export type AiAuditOutcome = z.infer<typeof aiAuditOutcomeSchema>;

/**
 * Một dòng audit-trace: chuỗi prompt(redacted) → intent → proposal(amount fact-block) →
 * idempotencyKey → ledger_transaction (join read-only). rawPrompt nullable (redaction-aware).
 */
export const aiAuditTraceItemSchema = z
  .object({
    auditId: z.string(),
    conversationId: z.string().nullable(),
    /** end-user gây tương tác (id — surface hiển thị ref/chip; null nếu không truy). */
    actorUserId: z.string().nullable(),
    /**
     * Display-name actor resolve server-side (T01 · @UC9135): username(social)→email→phone. null khi
     * actorUserId null / không resolve được → UI fallback uuid-slice. KHÔNG phơi PII ngoài name.
     */
    actorName: z.string().nullable(),
    groupId: z.string().nullable(),
    fundId: z.string().nullable(),
    /** câu chat thô — null cho vai KHÔNG đủ (redaction; chỉ SUPER_ADMIN thấy thô — DR-2). */
    rawPrompt: z.string().nullable(),
    /** cờ đã che (true ⇒ rawPrompt=null do redaction, KHÔNG phải vắng data). */
    promptRedacted: z.boolean(),
    detectedIntent: z.string(),
    proposalKind: z.string().nullable(),
    /** minor VND (fact-block API, no-float) → string; null cho intent read-only. */
    proposalAmountMinor: z.string().nullable(),
    idempotencyKey: z.string().nullable(),
    /** id bút toán ledger đã join qua idempotencyKey (read-only); null nếu không nối được. */
    ledgerTxId: z.string().nullable(),
    outcome: aiAuditOutcomeSchema,
    toolTier: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();
export type AiAuditTraceItem = z.infer<typeof aiAuditTraceItemSchema>;

/** Envelope list audit-trace (cursor pagination) — đối xứng repo {data, pageInfo}. */
export const aiAuditTraceListResponseSchema = z
  .object({
    data: z.array(aiAuditTraceItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type AiAuditTraceListResponse = z.infer<typeof aiAuditTraceListResponseSchema>;

/**
 * Detail read-only (US-AE-IMP2-T11-S6 · @UC9156) — GET /admin/ai/audit-trace/:id. Truy vết chi tiết
 * 1 mục: prompt(redacted) → intent → amount → idempotencyKey → ledgerTxId — CÙNG shape list item
 * (READ-ONLY). rawPrompt redaction-aware (chỉ SUPER_ADMIN thô). `.strict()` reject field lạ.
 * KHÔNG mở path mutate (`ai_audit_log` + ledger APPEND-ONLY — AI-INV-7 + G2).
 */
export const aiAuditTraceDetailResponseSchema = aiAuditTraceItemSchema;
export type AiAuditTraceDetailResponse = z.infer<typeof aiAuditTraceDetailResponseSchema>;

// ── safety-event ─────────────────────────────────────────────────────────────────────────────

/** Taxonomy loại vi phạm an toàn (đóng — DR-3). */
export const aiSafetyKindSchema = z.enum(["injection", "tool_scope", "fact_block", "escalation"]);
export type AiSafetyKind = z.infer<typeof aiSafetyKindSchema>;

/** Một dòng safety event trong board (ca bị chặn). detail đã redacted-at-write (no-PII thô). */
export const aiSafetyEventItemSchema = z
  .object({
    eventId: z.string(),
    conversationId: z.string().nullable(),
    auditId: z.string().nullable(),
    actorUserId: z.string().nullable(),
    /** Display-name actor resolve server-side (T01 · @UC9135). null → UI fallback uuid-slice. */
    actorName: z.string().nullable(),
    groupId: z.string().nullable(),
    kind: aiSafetyKindSchema,
    /** mô tả loại vi phạm (đã redacted no-PII). */
    detail: z.string(),
    blocked: z.boolean(),
    createdAt: z.string(),
  })
  .strict();
export type AiSafetyEventItem = z.infer<typeof aiSafetyEventItemSchema>;

/** Envelope list safety events (cursor pagination). */
export const aiSafetyEventListResponseSchema = z
  .object({
    data: z.array(aiSafetyEventItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type AiSafetyEventListResponse = z.infer<typeof aiSafetyEventListResponseSchema>;

// ── US-AIV2-0114 L08a (@UC9936 thống kê/phễu · @UC9937 lọc theo ngày + kết quả) ─────────────────
//
// Sau phát hành, admin cần MỘT con số mỗi tuần: người thật nói gì mà trợ lý không làm được, và mỗi trang đi được bao xa
// (lượt → phiếu → thẻ xác nhận → Đồng ý → xong). Mọi con số đọc từ `ai_audit_log` (dòng v2 của `AiTaskAuditWriter`) — trừ
// "lượt menu", vốn KHÔNG có dòng audit (lượt chưa mở phiếu thì không có taskId), nên đếm từ `ai_messages.kind='menu'` — chỉ
// ĐẾM, không đọc nội dung. Response thống kê không có một trường chữ nào của người dùng ⇒ cả 4 vai đọc được; câu thô vẫn chỉ ở
// danh sách truy vết và vẫn chỉ SUPER_ADMIN đọc thô (`canViewRawPrompt`, CLAUDE.md §4.3).

/**
 * Mã hoá `detectedIntent` của dòng audit v2 — MỘT nguồn cho bộ đếm (api) và preset lọc (admin). Writer (`AiTaskAuditWriter`)
 * là nơi ghi; `ai-audit-v2-intent-contract.spec.ts` gọi writer THẬT và so chuỗi nó ghi với các hằng này (chốt ở chỗ ghi ra).
 */
export const AI_AUDIT_V2_INTENT = Object.freeze({
  marker: "v2",
  input: "v2:input",
  envelopePrefix: "v2:envelope:",
  envelopeUnknown: "v2:envelope:unknown",
  transitionPrefix: "v2:transition:",
  /** Hậu tố của mọi chuyển TỚI thẻ xác nhận (`<từ>>>confirming`). */
  toConfirming: ">>confirming",
  /** Người bấm Đồng ý trên thẻ xác nhận. */
  agreed: "v2:transition:confirming>>executing",
  resultOk: "v2:result:ok",
  /** Tiền tố "mọi lỗi": khớp cả `v2:result:fail` (không mã) lẫn `v2:result:fail:<CODE>`. */
  resultFail: "v2:result:fail",
  resultFailCodePrefix: "v2:result:fail:",
} as const);

/** Mã lỗi ổn định, ngôn-ngữ-trung-tính (CLAUDE.md §7) — tập đóng; writer ném nếu mã không khớp. */
export const AI_AUDIT_RESULT_CODE_RE = /^[A-Z][A-Z0-9_]{0,63}$/;

/** Bộ lọc kết quả của danh sách truy vết. "menu" không có ở đây: lượt menu không có dòng audit (xem đầu mục). */
export type AiAuditResultFilter =
  | { readonly kind: "all" }
  | { readonly kind: "unknown" }
  | { readonly kind: "fail" }
  | { readonly kind: "failCode"; readonly code: string };

/** Bộ lọc kết quả → `detectedIntentPrefix` của GET /admin/ai/audit-trace. Mã lỗi ngoài tập đóng ⇒ ném (không đẩy chuỗi lạ lên query). */
export function aiAuditResultFilterPrefix(filter: AiAuditResultFilter): string | undefined {
  switch (filter.kind) {
    case "all":
      return undefined;
    case "unknown":
      return AI_AUDIT_V2_INTENT.envelopeUnknown;
    case "fail":
      return AI_AUDIT_V2_INTENT.resultFail;
    case "failCode":
      if (!AI_AUDIT_RESULT_CODE_RE.test(filter.code)) throw new Error("mã lỗi không hợp lệ");
      return `${AI_AUDIT_V2_INTENT.resultFailCodePrefix}${filter.code}`;
  }
}

/**
 * Ngày được tính theo lịch Việt Nam — MỘT nguồn với biên ngày của xuất sổ / lọc giao dịch (`vnDayStartUtc` · `vnYmdOf`, domain
 * `vn-calendar-day.ts`, REPORT-EXPORT-VN-DAY-BOUNDARY B1). Trước B1 bộ này dùng hằng `"+07:00"` riêng — bản thứ hai của cùng luật.
 */
const DAY_MS = 24 * 60 * 60 * 1000;
/** Trần khoảng thống kê (tính cả hai đầu) — trục ngày của response có tối đa chừng này phần tử. */
export const ADMIN_AI_AUDIT_STATS_MAX_DAYS = 92;
/** Trần số mã lỗi trả về (nhiều nhất trước); vượt ⇒ `failureCodesTruncated: true`. */
export const ADMIN_AI_AUDIT_STATS_MAX_CODES = 50;
/** Tuần mặc định của màn admin. */
export const ADMIN_AI_AUDIT_DEFAULT_DAYS = 7;

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Năm nhỏ nhất nhận vào. Review #1099 L5: `0000-01-01` qua được regex + ngày có thật nhưng Prisma/Postgres ném ⇒ 500. Goppy không có dữ
 * liệu trước 2000 nên chặn ở biên (400) thay vì để DB từ chối.
 */
export const ADMIN_AI_AUDIT_MIN_YEAR = 2000;

/**
 * Ngày lịch `YYYY-MM-DD` CÓ THẬT và dựng được cả hai biên giờ VN (`isVnCalendarYmd`, domain — MỘT vị-từ với `vnCalendarDaySchema` của
 * xuất sổ/lọc giao dịch), năm ≥ `ADMIN_AI_AUDIT_MIN_YEAR`.
 *
 * 🔴 Review #1120 MED-1: trước đây bộ này có `isRealDay` riêng (chỉ hỏi "có thật") ⇒ `to=9999-12-31` qua biên rồi `aiAuditDayRangeUtc` ném
 * `RangeError` (hết ngày = năm 10000) ⇒ 500 ở GET audit-trace + audit-stats. Nay 400 `VALIDATION_FAILED` ở biên.
 */
export const adminAiAuditDaySchema = z
  .string()
  .regex(DAY_RE)
  .refine(isVnCalendarYmd, { message: "ngày không có thật" })
  .refine((d) => Number(d.slice(0, 4)) >= ADMIN_AI_AUDIT_MIN_YEAR, { message: `năm phải từ ${ADMIN_AI_AUDIT_MIN_YEAR}` });

/** Thời điểm UTC của 00:00 giờ VN ngày `day` (`day` đã qua `adminAiAuditDaySchema` — ngày có thật; không thì ném, không đoán). */
export function aiAuditDayStartUtc(day: string): Date {
  const start = vnDayStartUtc(day);
  if (start === null) throw new RangeError(`ngày không hợp lệ: ${day}`);
  return start;
}

/** Khoảng ngày VN (cả hai đầu) → khoảng thời điểm UTC `[gte, lt)`; đầu nào vắng thì không chặn đầu đó. */
export function aiAuditDayRangeUtc(range: { readonly from?: string; readonly to?: string }): { gte?: Date; lt?: Date } {
  return {
    ...(range.from !== undefined ? { gte: aiAuditDayStartUtc(range.from) } : {}),
    ...(range.to !== undefined ? { lt: aiAuditDayEndExclusiveUtc(range.to) } : {}),
  };
}

/** Mốc hết ngày VN `day` (exclusive) — cùng luật `aiAuditDayStartUtc`. */
function aiAuditDayEndExclusiveUtc(day: string): Date {
  const end = vnDayEndExclusiveUtc(day);
  if (end === null) throw new RangeError(`ngày không hợp lệ: ${day}`);
  return end;
}

/** Ngày VN chứa thời điểm `at`. */
export function aiAuditVnDay(at: Date): string {
  return vnYmdOf(at);
}

/** Số ngày tính cả hai đầu (from ≤ to). */
function daySpan(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;
}

/** Liệt kê mọi ngày từ `from` tới `to` (cả hai đầu). */
export function aiAuditDaysInclusive(from: string, to: string): string[] {
  const start = Date.parse(`${from}T00:00:00Z`);
  return Array.from({ length: Math.max(0, daySpan(from, to)) }, (_, i) => new Date(start + i * DAY_MS).toISOString().slice(0, 10));
}

/** Tuần mặc định: 7 ngày VN kết thúc ở hôm nay (giờ VN). */
export function aiAuditDefaultWeek(now: Date): { from: string; to: string } {
  const to = aiAuditVnDay(now);
  const from = new Date(Date.parse(`${to}T00:00:00Z`) - (ADMIN_AI_AUDIT_DEFAULT_DAYS - 1) * DAY_MS).toISOString().slice(0, 10);
  return { from, to };
}

/** from ≤ to khi có cả hai đầu — MỘT vị-từ cho query danh sách (api DTO) và query thống kê. */
export function aiAuditFromNotAfterTo(r: { readonly from?: string; readonly to?: string }): boolean {
  return r.from === undefined || r.to === undefined || r.from <= r.to;
}
const fromNotAfterTo = aiAuditFromNotAfterTo;

/** Khoảng ngày tuỳ chọn cho danh sách truy vết (@UC9937): mỗi đầu tuỳ chọn; có cả hai thì from ≤ to. */
export const aiAuditDayRangeSchema = z
  .object({ from: adminAiAuditDaySchema.optional(), to: adminAiAuditDaySchema.optional() })
  .strict()
  .refine(fromNotAfterTo, { message: "from phải trước hoặc bằng to", path: ["from"] });

/** Query GET /admin/ai/audit-stats (@UC9936): bắt buộc cả hai đầu, from ≤ to, tối đa `ADMIN_AI_AUDIT_STATS_MAX_DAYS` ngày. */
export const aiAuditStatsQuerySchema = z
  .object({ from: adminAiAuditDaySchema, to: adminAiAuditDaySchema })
  .strict()
  .refine(fromNotAfterTo, { message: "from phải trước hoặc bằng to", path: ["from"] })
  .refine((r) => r.from > r.to || daySpan(r.from, r.to) <= ADMIN_AI_AUDIT_STATS_MAX_DAYS, {
    message: `khoảng tối đa ${ADMIN_AI_AUDIT_STATS_MAX_DAYS} ngày`,
    path: ["to"],
  });
export type AiAuditStatsQuery = z.infer<typeof aiAuditStatsQuerySchema>;

const countSchema = z.number().int().nonnegative();

/** Một ngày VN. `inputs` = dòng `v2:input` (mỗi câu/nút người dùng đưa vào một phiếu); `menu` = lượt trợ lý trả menu (mọi lý do). */
export const aiAuditStatsDaySchema = z
  .object({ day: z.string(), inputs: countSchema, unknown: countSchema, resultOk: countSchema, resultFail: countSchema, menu: countSchema })
  .strict();

/** Một mã lỗi của bước kết quả; `code: null` = lỗi không kèm mã. Mã theo tập đóng của writer (review #1099 L4). */
export const aiAuditStatsFailureCodeSchema = z
  .object({ code: z.string().regex(AI_AUDIT_RESULT_CODE_RE).nullable(), count: countSchema })
  .strict();

/**
 * Phễu một trang (trang của phiếu việc, `ai_tasks.pageId`), đếm trên dòng audit TRONG khoảng: `turns` = dòng input ·
 * `tasks` = số phiếu · `confirmCards` = phiếu tới thẻ xác nhận · `agreed` = phiếu người bấm Đồng ý · `ok`/`failed` = phiếu có kết quả.
 */
export const aiAuditStatsPageSchema = z
  .object({ pageId: z.string().max(AI_PAGE_ID_MAX_CHARS).regex(AI_PAGE_ID_RE), turns: countSchema, tasks: countSchema, confirmCards: countSchema, agreed: countSchema, ok: countSchema, failed: countSchema })
  .strict();

export const aiAuditStatsResponseSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    days: z.array(aiAuditStatsDaySchema),
    failureCodes: z.array(aiAuditStatsFailureCodeSchema),
    failureCodesTruncated: z.boolean(),
    pages: z.array(aiAuditStatsPageSchema),
  })
  .strict();
export type AiAuditStatsDay = z.infer<typeof aiAuditStatsDaySchema>;
export type AiAuditStatsFailureCode = z.infer<typeof aiAuditStatsFailureCodeSchema>;
export type AiAuditStatsPage = z.infer<typeof aiAuditStatsPageSchema>;
export type AiAuditStatsResponse = z.infer<typeof aiAuditStatsResponseSchema>;
